/// <amd-module name="Build/Pipelines/Tabs/Runs/Stores/RunsStore" />
import { BuildSignalRActionHub, IBuildUpdatedPayload, IStagesUpdatedPayload } from "Actions/SignalR";
import { Build, BuildStatus, RetentionLease, TaskResult, TimelineRecordState } from "Build/Client/Contracts/Build";
import { FlatGraphRecord, RetentionLeaseData, RunsListItemData } from "Build/Client/Contracts/DataProvider";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import { handleError } from "Build/Common/Library/ErrorHandler";
import { TimelineRecordUpdate } from "Build/Common/Library/Types";
import { ContributionDataActionCreator, ContributionDataActionHub, DataParser } from "Build/Flux/Actions/ContributionData";
import { ContributionSource } from "Build/Flux/Sources/Contribution";
import { Store } from "Build/Flux/Store";
import { ISourceProviderService } from "Build/SourceProviders/SourceProvider";
import { BatchingDelayInMs, MaxUpdates, RunFilterKeys } from "Constants";
import { getTFSPageData } from "Tfs/Platform/Page";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssIdentityService } from "VSS/Platform/Identity";
import { IVssPageService } from "VSS/Platform/Page";
import { INewBranchFilter } from "../RunsFilterBar.types";
import { IAppliedFilterStrings, RunsAppliedFilterStore } from "./RunsAppliedFilterStore";

export interface IRunsProviderData {
    runs: RunsListItemData[];
    continuationToken: string;
}

export interface IRunsStoreProps {
    pageContext: IVssPageContext;
    contributionActionHub: ContributionDataActionHub<IRunsProviderData>;
    appliedFilterStore: RunsAppliedFilterStore;
    signalRActionHub: BuildSignalRActionHub;
    definitionId?: number;
    definitionScope?: string;
    getBranchesOnNewRun?: (branches: INewBranchFilter[]) => void;
}

export class RunsStore extends Store {
    private _branches: INewBranchFilter[] = [];
    private _contributionActionCreator: ContributionDataActionCreator<IRunsProviderData>;
    private _runs: RunsListItemData[] = [];
    private _continuationToken: string | undefined;
    private _definitionId: number | undefined;
    private _definitionScope: string | undefined;
    private _isLoading = true;
    private _appliedFilterStore: RunsAppliedFilterStore;
    private _runsWithUpdates: number[] = [];
    private _newRuns: number[] = [];
    private _deletedRuns: number[] = [];
    private _timerManagement: TimerManagement;
    private _signalRActionHub: BuildSignalRActionHub;
    private _initialRouteValues: { [key: string]: string } = {};
    private _buildClient: IBuildRestClient;
    private _projectId: string;
    private _userId: string;
    private _pageContext: IVssPageContext;
    private _identityService: IVssIdentityService;
    private _sourceProviderService: ISourceProviderService;
    private _repositoryToProjectNameMap: { [key: string]: Promise<string | null> } = {};
    private _getBranchesOnNewRun?: (branches: INewBranchFilter[]) => void;

    constructor(options: IRunsStoreProps) {
        super();

        this._appliedFilterStore = options.appliedFilterStore;
        this._definitionId = options.definitionId;
        this._definitionScope = options.definitionScope;

        this._signalRActionHub = options.signalRActionHub;
        this._signalRActionHub.stagesUpdated.addListener(this._updateStages);
        this._signalRActionHub.buildUpdated.addListener(this._buildUpdated);
        this._getBranchesOnNewRun = options.getBranchesOnNewRun;

        this._contributionActionCreator = new ContributionDataActionCreator({
            pageContext: options.pageContext,
            actionHub: options.contributionActionHub,
            dataParser: this._dataParser
        });

        this._appliedFilterStore.addListener(this._onFiltersUpdated);

        this._timerManagement = new TimerManagement();
        this._triggerRunsUpdateBatch = this._timerManagement.debounce(this._triggerRunsUpdateBatch, BatchingDelayInMs);

        this._sourceProviderService = options.pageContext.getService<ISourceProviderService>("ISourceProviderService");

        const source = new ContributionSource({
            pageContext: options.pageContext
        });
        const data = source.getData("ms.vss-build-web.runs-data-provider") as IRunsProviderData;
        if (data) {
            this._isLoading = false;
            this._runs = data.runs || [];
            this._repositoryToProjectNameMap = this._mapRepositoriesToProjectNames();
            this._continuationToken = data.continuationToken || undefined;
            this._getNewBranches();
        }

        this._buildClient = options.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
        this._pageContext = options.pageContext;

        const tfsPageData = getTFSPageData(options.pageContext);
        if (tfsPageData && tfsPageData.project) {
            this._projectId = tfsPageData.project.id;
        }

        const vssPageService = options.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();

        if (vssPageData) {
            this._userId = vssPageData.user.id;
        }

        this._identityService = options.pageContext.getService<IVssIdentityService>("IVssIdentityService");

        this._populateInitialRoutes(options.pageContext);
        this._populateAppliedFiltersFromInitialRouteValues();
    }

    public dispose() {
        super.dispose();

        this._signalRActionHub.stagesUpdated.removeListener(this._updateStages);
        this._signalRActionHub.buildUpdated.removeListener(this._buildUpdated);
    }

    public fetchData(refresh: boolean = false) {
        if (refresh) {
            this._runs = [];
            this._runsWithUpdates = [];
            this._newRuns = [];
            this._continuationToken = "";
            this._isLoading = true;

            let routeValues: { [key: string]: string } = {};
            if (this._appliedFilterStore.hasAppliedFilters()) {
                routeValues = this._appliedFilterStore.addFilterRoutes(routeValues);
            }
            // Add definition to route values unless this is page load scenario
            // On page load the data provider is called with no request parameters.  We want to call with the same parameters
            // the first call so that we hit the cache instead of making an extra trip to data provider.
            if (refresh && !!this._definitionId) {
                routeValues.definitionId = this._definitionId.toString();
            }

            if (!!this._definitionScope) {
                routeValues.definitionScope = this._definitionScope;
            }
            
            this._contributionActionCreator.fetchContributionData("ms.vss-build-web.runs-data-provider", routeValues, refresh, this._onDataAvailable);

            this.emitChanged();
        }
    }

    private _populateAppliedFiltersFromInitialRouteValues() {
        // If we don't have initial routes or already have applied filters, skip
        if (this._appliedFilterStore.hasAppliedFilters() || Object.keys(this._initialRouteValues).length == 0) {
            return;
        }

        var filterStrings : IAppliedFilterStrings = {
            repository: this._initialRouteValues[RunFilterKeys.RepositoryFilter],
            branch: this._initialRouteValues[RunFilterKeys.BranchFilter],
            keywords: this._initialRouteValues[RunFilterKeys.KeywordFilter],
            requestedFor: this._initialRouteValues[RunFilterKeys.RequestedForFilter],
            result: this._initialRouteValues[RunFilterKeys.ResultFilter],
            status: this._initialRouteValues[RunFilterKeys.StatusFilter],
            tags: this._initialRouteValues[RunFilterKeys.TagFilter]
        };

        for (const [routeKey, routeValue] of Object.entries(filterStrings)) {
            if (routeValue !== undefined) {
                this._appliedFilterStore.filter.setFilterItemState(routeKey, {value: routeValue});
            }
        }
        
        this._appliedFilterStore.filter.applyChanges();
        this.emitChanged();
    }

    public fetchMoreRuns() {
        if (!this._isLoading && !!this._continuationToken) {
            let routeValues: { [key: string]: string } = {
                continuationToken: this._continuationToken
            };
            if (!!this._definitionId) {
                routeValues.definitionId = this._definitionId.toString();
            }
            if (!!this._definitionScope) {
                routeValues.definitionScope = this._definitionScope;
            }
            if (this._appliedFilterStore.hasAppliedFilters()) {
                routeValues = this._appliedFilterStore.addFilterRoutes(routeValues);
            }

            this._isLoading = true;
            this._contributionActionCreator.fetchContributionData(
                "ms.vss-build-web.runs-data-provider",
                routeValues,
                true,
                this._onMoreRunsAvailable
            );
            this.emitChanged();
        }
    }

    public getRunData(): RunsListItemData[] {
        return this._runs;
    }

    public getProjectNamePromise(repoId: string): Promise<string | null> {
        return this._repositoryToProjectNameMap[repoId] || new Promise(() => null);
    }

    public hasMore(): boolean {
        return !!this._continuationToken;
    }

    public isLoading(): boolean {
        return this._isLoading;
    }

    public getNewRunsCount(): number {
        return this._newRuns.length;
    }

    public cancelRun(runId: number): void {
        this._buildClient
            .updateBuild(
                {
                    status: BuildStatus.Cancelling
                } as Build,
                this._projectId,
                runId
            )
            .then(() => {}, handleError);
    }

    public deleteRun(runId: number): void {
        this._buildClient.deleteBuild(this._projectId, runId).then(() => {
            this._deletedRuns.push(runId);
            const itemIndex = this._runs.findIndex((value: RunsListItemData) => (value.run.id === runId));
            if (itemIndex >= 0) {
                this._runs.splice(itemIndex, 1);
                this.emitChanged();
            }
        }, handleError);
    }

    public retainRun(definitionId: number, runId: number): void {
        const infiniteRetentionLease = 365 * 1000; // Use 1000 years as infinite retention lease daysValid
        if (this._userId) {
            const leaseOwner = "User:" + this._userId;
            this._buildClient
                .addRetentionLeases(
                    [{ definitionId: definitionId, runId: runId, ownerId: leaseOwner, daysValid: infiniteRetentionLease, protectPipeline: false }],
                    this._projectId
                )
                .then((addedLease: RetentionLease[]) => {
                    if (addedLease && addedLease.length === 1) {
                        const itemIndex = this._runs.findIndex((value: RunsListItemData) => value.run.id === runId);
                        if (itemIndex >= 0) {
                            const userDetails = this._identityService.getIdentityDetails(this._userId);

                            this._runs[itemIndex].run.retentionLeases.push({
                                id: addedLease[0].leaseId,
                                validUntil: addedLease[0].validUntil,
                                leaseType: "User",
                                ownerDisplayName: userDetails.displayName,
                                leaseOwnerIdentityRef: {
                                    id: this._userId,
                                    displayName: userDetails.displayName,
                                    imageUrl: userDetails.imageUrl
                                } as IdentityRef
                            });
                            this.emitChanged();
                        }
                    }
                }, handleError);
        }
    }

    public stopRetainingRun(definitionId: number, runId: number): void {
        if (this._userId) {
            const leaseOwner = "User:" + this._userId;
            this._buildClient.getRetentionLeasesByOwnerId(this._projectId, leaseOwner, definitionId, runId).then((leases: RetentionLease[]) => {
                const leaseIds: number[] = [];
                leases.forEach((lease: RetentionLease) => {
                    leaseIds.push(lease.leaseId);
                });
                this.deleteRetentionLeases(runId, leaseIds);
            }, handleError);
        }
    }

    public deleteRetentionLeases(runId: number, leaseIds: number[]): void {
        if (leaseIds.length > 0) {
            this._buildClient.deleteRetentionLeasesById(this._projectId, leaseIds).then(() => {
                const itemIndex = this._runs.findIndex((value: RunsListItemData) => value.run.id === runId);
                if (itemIndex >= 0) {
                    leaseIds.forEach((leaseId: number) => {
                        const index = this._runs[itemIndex].run.retentionLeases.findIndex((lease: RetentionLeaseData) => lease.id === leaseId);
                        this._runs[itemIndex].run.retentionLeases.splice(index, 1);
                    });
                    this.emitChanged();
                }
            }, handleError);
        }
    }

    private _dataParser: DataParser<IRunsProviderData> = (data: IRunsProviderData) => {
        return data;
    };

    private _getNewBranches = () => {
        this._runs.forEach((run: RunsListItemData) => {
            this._branches.push({ branchName: run.run.sourceBranch, repositoryId: run.run.repository.id });
        });
        this._branches = this._branches.filter((elem, index) => this._branches.findIndex(obj => obj.repositoryId == elem.repositoryId && obj.branchName == elem.branchName) === index);
        this._getBranchesOnNewRun && this._getBranchesOnNewRun(this._branches);
    };

    private _populateInitialRoutes = (pageContext: IVssPageContext) => {
        const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const repositoryFilter = historyService.getState()[RunFilterKeys.RepositoryFilter];
        const branchFilter = historyService.getState()[RunFilterKeys.BranchFilter];
        const keywordFilter = historyService.getState()[RunFilterKeys.KeywordFilter];
        const requestedForFilter = historyService.getState()[RunFilterKeys.RequestedForFilter];
        const resultFilter = historyService.getState()[RunFilterKeys.ResultFilter];
        const statusFilter = historyService.getState()[RunFilterKeys.StatusFilter];
        const tagFilter = historyService.getState()[RunFilterKeys.TagFilter];
        this._initialRouteValues[RunFilterKeys.RepositoryFilter] = repositoryFilter;
        this._initialRouteValues[RunFilterKeys.BranchFilter] = branchFilter;
        this._initialRouteValues[RunFilterKeys.KeywordFilter] = keywordFilter;
        this._initialRouteValues[RunFilterKeys.RequestedForFilter] = requestedForFilter;
        this._initialRouteValues[RunFilterKeys.ResultFilter] = resultFilter;
        this._initialRouteValues[RunFilterKeys.StatusFilter] = statusFilter;
        this._initialRouteValues[RunFilterKeys.TagFilter] = tagFilter
            ?.split(",")
            .map((tag: string) => decodeURIComponent(tag))
            .join();
    };

    private _triggerRunsUpdateBatch = () => {
        if (this._runsWithUpdates.length > 0) {
            const routeValues: { [key: string]: string } = {
                buildIds: this._runsWithUpdates.join(",")
            };

            this._runsWithUpdates = [];
            this._contributionActionCreator.fetchContributionData(
                "ms.vss-build-web.runs-data-provider",
                routeValues,
                true,
                this._onUpdatedRunsAvailable
            );
        }
    };

    private _onFiltersUpdated = () => {
        if (!this._appliedFilterStore.hasAppliedFilters() || !this._appliedFilterStore.doFiltersMatch(this._initialRouteValues, true)) {
            this._initialRouteValues = {};
            this.fetchData(true);
        }
    };

    private _onDataAvailable = (data: IRunsProviderData, routeValues?: { [key: string]: string }): void => {
        if (this._appliedFilterStore.doFiltersMatch(routeValues || {}, false)) {
            // in case any updates came in while fetching runs, clear again.  this is data is current
            this._newRuns = [];

            this._isLoading = false;
            this._runs = data.runs || [];
            this._repositoryToProjectNameMap = this._mapRepositoriesToProjectNames();

            this._continuationToken = data.continuationToken || undefined;
            this._getNewBranches();
            this.emitChanged();
        }
    };

    private _onMoreRunsAvailable = (data: IRunsProviderData, routeValues?: { [key: string]: string }): void => {
        this._isLoading = false;
        this._runs = this._runs.concat(data.runs);
        this._repositoryToProjectNameMap = this._mapRepositoriesToProjectNames();
        this._continuationToken = data.continuationToken || undefined;
        this._getNewBranches();
        this.emitChanged();
    };

    private _buildUpdated = (payload: IBuildUpdatedPayload): void => {
        if (this._definitionId && payload.definitionId != this._definitionId) {
            return;
        }

        if (this._definitionScope && payload.definitionScope != this._definitionScope) {
            return;
        }
        
        if (this._runs.filter(r => r.run && r.run.id === payload.buildId).length > 0) {
            if (this._runsWithUpdates.indexOf(payload.buildId) < 0) {
                this._runsWithUpdates.push(payload.buildId);
                this._triggerRunsUpdateBatch();
            }
        }
        // add new run if we do not already have it and there are no filters applied
        else if (!this._deletedRuns.filter(r => r === payload.buildId).length && !this._appliedFilterStore.hasAppliedFilters() && this._newRuns.length < MaxUpdates && this._newRuns.indexOf(payload.buildId) < 0) {

            this._newRuns.push(payload.buildId);
            this.emitChanged();
        }
    };

    private _onUpdatedRunsAvailable = (data: IRunsProviderData): void => {
        if (data && data.runs && data.runs.length > 0) {
            const idToIndex: { [key: number]: number } = {};
            this._runs.forEach((runData, index) => {
                if (runData.run) {
                    idToIndex[runData.run.id] = index;
                }
            });

            data.runs.forEach(update => {
                if (update.run) {
                    const indexToReplace = idToIndex[update.run.id];
                    if (indexToReplace !== undefined) {
                        this._runs[indexToReplace] = update;
                    }
                }
            });

            this.emitChanged();
        }
    };

    private _updateStages = (payload: IStagesUpdatedPayload): void => {
        let loadedBuildIds = this.getLoadedRunsId();
        if (payload && payload.buildId && payload.stageUpdates && loadedBuildIds.find(buildId => buildId === payload.buildId)) {
            this._updateRuns(payload.buildId.toString());
        }
    };

    private _updateRuns = (buildIds: string): void => {
        const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService
            .getDataAsync<IRunsProviderData>("ms.vss-build-web.runs-data-provider", { buildIds: buildIds }, true)
            .then((data: IRunsProviderData | undefined) => {
                let madeChanges = false;
                data?.runs?.forEach(run => {
                    const runIndex = this._runs.findIndex(r => !!r.run && r.run.id == run.run.id);
                    if(runIndex != -1) {
                        this._runs[runIndex] = run;
                        madeChanges = true;
                        
                        const runCompleted = this._runs[runIndex].stages.every((stage: FlatGraphRecord) => stage.state !== TimelineRecordState.Completed);
                        
                        if (runCompleted && this._runs[runIndex].run && !this._runs[runIndex].run.finishTime) {
                            // This will help offset the delay between the stage finishing and us fetching the build updates.
                            // Otherwise Duration could keep ticking for ~3 seconds and then jump back
                            this._runs[runIndex].run.finishTime = new Date();
                        }
                    }
                });
        
                if (madeChanges) {
                    this.emitChanged();
                }
            });
    };

    private _fixRecordSerialization(stage: TimelineRecordUpdate): TimelineRecordUpdate {
        let startTime = stage.startTime;
        if (typeof stage.startTime === "string") {
            startTime = new Date(stage.startTime as string);
        }

        let finishTime = stage.finishTime;
        if (typeof stage.finishTime === "string") {
            finishTime = new Date(stage.finishTime as string);
        }

        let state = stage.state;
        if (typeof stage.state === "string") {
            switch ((stage.state as string).toLowerCase()) {
                case "pending":
                    state = TimelineRecordState.Pending;
                    break;
                case "inprogress":
                    state = TimelineRecordState.InProgress;
                    break;
                case "completed":
                    state = TimelineRecordState.Completed;
                    break;
            }
        }

        let result = stage.result;
        if (typeof stage.result === "string") {
            switch ((stage.result as string).toLowerCase()) {
                case "succeeded":
                    result = TaskResult.Succeeded;
                    break;
                case "succeededwithissues":
                    result = TaskResult.SucceededWithIssues;
                    break;
                case "failed":
                    result = TaskResult.Failed;
                    break;
                case "canceled":
                    result = TaskResult.Canceled;
                    break;
                case "skipped":
                    result = TaskResult.Skipped;
                    break;
                case "abandoned":
                    result = TaskResult.Abandoned;
                    break;
            }
        }

        return {
            id: stage.id,
            startTime: startTime,
            finishTime: finishTime,
            state: state,
            result: result
        };
    }

    private getLoadedRunsId(): number[] {
        return this._runs.map(r => r.run.id);
    }

    private _mapRepositoriesToProjectNames(): { [key: string]: Promise<string | null> } {
        if (this._runs) {
            const repositoryToProjectNameMap: { [key: string]: Promise<string | null> } = {};

            // create promise that will return projectName for each unique repo
            this._runs.forEach(run => {
                const repo = run.run.repository;
                if (!repositoryToProjectNameMap[repo.id]) {
                    const sourceProvider = this._sourceProviderService.getSourceProvider(repo.type);
                    repositoryToProjectNameMap[repo.id] = sourceProvider.getProjectName(repo.id);
                }
                const triggeringRepo = run.run.triggeringRepository;
                if (triggeringRepo && !repositoryToProjectNameMap[triggeringRepo.id]) {
                    const sourceProvider = this._sourceProviderService.getSourceProvider(triggeringRepo.type);
                    repositoryToProjectNameMap[triggeringRepo.id] = sourceProvider.getProjectName(triggeringRepo.id);
                }
            });

            return repositoryToProjectNameMap;
        }

        return {};
    }
}
